'use client';

import { Link } from '@coolblue-development/freddy';
import selectItemInItemCardTrigger from '@coolblue-development/ga4-events/triggers/ItemCard/selectItemInItemCardTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PromotionalProductImage } from '@coolblue-development/next-patterns/core';
import { DebugInfo } from '@features/filter/types';
import { FilterPagePromoIcon, PromotionalContent } from '@features/product/typings';
import legacyPromotionalContentFactory from '@features/shared/PromotionalContent/legacyPromotionalContentFactory';
import { promotionalContentFactory } from '@features/shared/PromotionalContent/promotionalContentFactory';

import { PrimaryIcon } from './PrimaryIcon';
import { ScoreTag } from './ScoreTag';

interface ProductCardImageProps {
  productPageLink: string;
  isInternalUser: boolean;
  debugInfo: DebugInfo;
  name: string;
  id: string;
  primaryImage: {
    id: string;
  };
  primaryPromoIcon?: FilterPagePromoIcon;
  secondaryPromoIcon?: FilterPagePromoIcon;
  index: number;
  total_indices: number;
  promotionalContent: PromotionalContent;
  shouldUsePromotionalContent: boolean;
}

export const Image = ({
  isInternalUser,
  debugInfo,
  name,
  id,
  primaryImage,
  primaryPromoIcon,
  secondaryPromoIcon,
  index,
  total_indices,
  productPageLink,
  promotionalContent,
  shouldUsePromotionalContent
}: ProductCardImageProps) => {
  const { trackEvent } = useAnalytics();

  const legacyPromotionalContent = legacyPromotionalContentFactory(
    {
      image: secondaryPromoIcon?.imageId
        ? {
            id: secondaryPromoIcon.imageId,
            collectionName: 'content'
          }
        : null,
      displayText: secondaryPromoIcon?.text || null,
      explanation: secondaryPromoIcon?.text || null
    },
    name
  );

  return (
    <Link
      href={productPageLink}
      sx={{ position: 'relative', display: 'block' }}
      onClick={() =>
        trackEvent(
          selectItemInItemCardTrigger({
            index: index,
            total_indices: total_indices,
            method: 'image',
            item_id: Number(id)
          })
        )
      }
    >
      {isInternalUser && <ScoreTag debugInfo={debugInfo} productName={name} id={id} />}
      <PromotionalProductImage
        alt={name}
        size='medium'
        imageId={primaryImage.id}
        labelsPosition={{ xs: 'belowImage', lg: 'middle' }}
        {...(primaryPromoIcon && {
          primaryLabel: <PrimaryIcon promoIcon={primaryPromoIcon} />
        })}
        promotionalContent={
          shouldUsePromotionalContent
            ? promotionalContentFactory(promotionalContent)
            : legacyPromotionalContent
        }
      />
    </Link>
  );
};
